import { UserService } from './pages/user/services/user.service';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { AppLayoutComponent } from './layout/app.layout.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { AuthenticationComponent } from './pages/authentication/authentication.component';
import { DocApiComponent } from './pages/docs/doc-api/doc-api.component';
import { AuthService } from './services/guard/auth.service';
import { ChannelService } from './pages/import/service/channel.service';
import { NgxPermissionsService } from 'ngx-permissions';
import { LocalService } from './services/storage/local.service';
import { Subject } from 'rxjs';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { MessageService } from 'primeng/api';
import { PrimeNG } from 'primeng/config';
import { ToastModule } from 'primeng/toast';
import { environment } from '../environments/environment';
import { LemonLearningService } from './services/lemon/lemon-learning.service';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    AppLayoutComponent,
    ButtonModule,
    AuthenticationComponent,
    DocApiComponent,
    ToastModule,
    TranslateModule,
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  providers: [NgxPermissionsService, MsalService, MsalBroadcastService, MessageService],
})
export class AppComponent implements OnInit, OnDestroy {
  loggedIn;
  url: string = '';
  accessApp = true;
  env;
  private readonly _destroying$ = new Subject<void>();

  constructor(
    private readonly translate: TranslateService,
    public userService: UserService,
    private readonly authService: AuthService,
    private readonly router: Router,
    private readonly channelService: ChannelService,
    private readonly permissionsService: NgxPermissionsService,
    private readonly localStore: LocalService,
    private readonly config: PrimeNG,
    private readonly lemonLearning: LemonLearningService,
  ) {
    this.translate.addLangs(['fr', 'en']);
    this.translate.get('primeng').subscribe((res) => this.config.setTranslation(res));
  }

  ngOnInit(): void {
    this.env = environment.environment;
    if (this.authService.isAuthenticated()) {
      this.processLogin();
    } else {
      this.loggedIn = false;
    }
  }

  processLogin() {
    this.userService.processUser().subscribe({
      next: (res: any) => {
        if (!res) return;

        this.setupUser(res);

        if (this.isAccessAppOnlyUser(res)) {
          this.accessApp = false;
          this.loggedIn = true;
          return;
        }

        this.setupPermissions(res);
        this.setupChannel(res);
        this.handleNavigation(res);
        this.lemonLearning.initializeLemonLearning(
          res.firstname + ' ' + res.lastname,
          res.email,
          res.defaultChannel?.channelName || 'FR',
          res.defaultRole?.roleCode
        );

        this.loggedIn = true;
      },
      error: (res) => {
        this.authService.logout(res);
      }
    });
  }

  private setupUser(user: any): void {
    this.userService.setUser(user);
    this.translate.use(user?.defaultLanguage?.languageCode.toLowerCase());
  }

  private isAccessAppOnlyUser(user: any): boolean {
    return user?.relatedRoles?.length === 1 &&
           user?.relatedRoles[0].roleCode === 'ROLE_ACCESS_APP';
  }

  private setupPermissions(user: any): void {
    const permissions = user.relatedRoles
      .flatMap(role => role.permissions)
      .map(permission => permission.permissionCode);
    this.permissionsService.loadPermissions(permissions);
  }

  private setupChannel(user: any): void {
    const selectedChannel = localStorage.getItem('selectedChannel');

    if (selectedChannel && selectedChannel !== 'undefined') return;



    const channel = this.localStore.getItem('selectedChannel');
    if (channel === 'undefined') {
      if (this.userService.isOperator) {
        this.channelService.setSelectedChannel(user.defaultChannel);
      }else {
        this.channelService.setSelectedChannel(user.defaultShop.shopChannel);
      }

    }
  }

  private handleNavigation(user: any): void {
    if (user.defaultHomePage === null || this.localStore.getItem('redirect') !== undefined) {
      return;
    }

    const route = user?.operator ? ['operator/catalog'] : ['seller/catalog'];
    this.router.navigate(route);
    this.localStore.setItem('redirect', 'done');
  }

  logout() {
    this.authService.logout();
  }

  login(role: string) {
    this.authService.login(role);
  }

  ngOnDestroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }

  checkRouteAndRedirect(): boolean {
    this.url = this.router.url;
    const targetString = 'docapi';
    return this.url.includes(targetString);
  }
}
